import '@framework/browser/polyfill';
import cn from 'classnames';
import NextApp, { AppProps, AppInitialProps, AppContext, AppType } from 'next/app';
import DefaultSeo from '@framework/seo';
import { roboto, openSans } from '@framework/font';
import ErrorBoundary from '@framework/error-handling/ErrorBoundary';
import ApplicationFallback from '@framework/error-handling/ErrorBoundary/ApplicationFallback';
import Layout from '@/creditflowresearch/ui/common/components/Layout';
import Bootstrap from '@/creditflowresearch/infrastructure/bootstrap/Bootstrap';
import { configureApp } from '@framework/bootstrap';
import Head from 'next/head';
import Container from '@framework/di/Container';
import Favicon from '@finsight/favicon';
import CookiesBanner from '@framework/cookie-consent/CookiesBanner';
import GlobalProps from '@framework/router/Next/GlobalProps';
import { IGlobalProps } from '@framework/router/Next/withGlobalProps';
import ApplicationGlobalProps from '@/creditflowresearch/application/ApplicationGlobalProps';
import { IDictionariesState } from '@/creditflowresearch/application/Dictionaries/helpers';
import type { TPageDictionaries } from '@/creditflowresearch/application/Dictionaries/DictionaryRegistry';
import type { IHeaderMenuItems } from '@/creditflowresearch/domain/vo/IHeaderMenuItems';
import { IUserPlanState } from '@/creditflowresearch/application/User/UserPlan/types';
import TimeZoneContextProvider from '@framework/timezone/TimeZoneContext';
import { ITimeZoneInfo } from '@/creditflowresearch/application/User/UserTimeZone/types';
import FiltersContextWrapper from '@framework/ui/Organisms/Filters/FiltersContextWrapper';
import { rootModules } from '@/creditflowresearch/application/DI/rootModules';
import { footerCollection } from '@/creditflowresearch/ui/common/components/Footer/footerCollection';
import PageLoadInstrumentation from '@framework/otel/Next/PageLoadInstrumentation';
import PageNavigationInstrumentation from '@framework/otel/Next/PageNavigationInstrumentation';
import { PagesProgressProvider } from '@bprogress/next';
import { DomainRegistryProvider } from '@/creditflowresearch/application/DomainRegistryProvider';
import type { IDomainDictionaries } from '@/creditflowresearch/domain';
import { ThemeProvider } from '@creditflowresearch/next-themes/src';
import FeatureToggle from '@framework/ui/Organisms/FeatureToggle/Components';
import { ASK_AI } from '@creditflowresearch/config/featureToggle';
import AiSidebarContextProvider from '@/creditflowresearch/application/AiSidebar/AiSidebarContext';
import { IAiSidebarState } from '@/creditflowresearch/domain/vo/AiSidebar';
import AskAiChatProvider from '@/creditflowresearch/application/AskAiChat/context/AskAiChatContext';
import AiSidebar from '@/creditflowresearch/ui/common/components/organisms/AiSidebar';
import MetaTagsContextProvider from '@/creditflowresearch/application/MetaTags/MetaTagsContext';
import { PROGRESS_BAR_PARENT_ID } from '@/creditflowresearch/ui/common/components/Layout/constants';

import { THEME_STORAGE_KEY } from '@creditflowresearch/config/themeConfig';
import { isDarkThemeSupported } from '@/creditflowresearch/application/helpers/isDarkThemeSupported';
import PageNotFoundPage from '@/creditflowresearch/pages/404';
import localStyles from './styles.module.scss';
import '@/creditflowresearch/ui/styles/globalStyles.scss';
import '@creditflowresearch/uilib/styles/tokens/theme.scss';
import '@creditflowresearch/uilib/Picker/BaseDatePicker/vendor.scss';

interface IFinsightProps extends IGlobalProps {
  dictionaries?: IDictionariesState,
  userPlanState?: IUserPlanState,
  timeZoneInfo?: ITimeZoneInfo,
  aiSidebarState?: IAiSidebarState,
  domainDictionaries?: IDomainDictionaries,
  pageDictionaries?: TPageDictionaries,
  headerMenuItems?: IHeaderMenuItems | null,
  isNotFound?: boolean,
}

const AppComponent = ({ Component, pageProps, container, router }: AppProps<IFinsightProps> & {
  container: Container,
}) => {
  const { pathname, asPath } = router;
  const PageComponent = pageProps?.isNotFound ? PageNotFoundPage : Component;
  const isFullWidth = pathname.includes('/full-width') && !pageProps?.isNotFound;
  const pagesProgressProps = {
    height: '4px',
    color: 'linear-gradient(248deg, #E84699 0%, #875DB6 100%);',
    options: { showSpinner: false, parent: `#${ PROGRESS_BAR_PARENT_ID }` },
    shallowRouting: true,
  };

  const isDarkThemeEnabled = isDarkThemeSupported(asPath, pageProps?.cookies);

  return (
    <>
      <Favicon />
      <Head>
        <title />
        <meta charSet="utf-8" />
        <meta name="referrer" content="no-referrer-when-downgrade" />
        <meta httpEquiv="Cache-Control" content="no-store, no-cache, must-revalidate, proxy-revalidate, max-age=0" />
        <meta httpEquiv="Pragma" content="no-store" />
        <meta httpEquiv="Expires" content="0" />
        <meta content="initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no" name="viewport" />
        <meta name="HandheldFriendly" content="true" />
      </Head>
      <ErrorBoundary fallback={ ApplicationFallback }>
        { /* TODO: dark-theme — remove forcedTheme and defaultTheme once all pages support dark theme.
            - defaultTheme="light" ensures new users start with light mode (prevents FOUC with dark OS preference)
            - forcedTheme="light" on non-dark pages applies light without writing to localStorage,
              preserving the user's dark theme choice for dark-enabled pages
            - Dark theme is only active when: page is in DARK_THEME_PAGES AND darkThemeEnabled cookie is set */ }
        <ThemeProvider
          storageKey={ THEME_STORAGE_KEY }
          defaultTheme="light"
          forcedTheme={ isDarkThemeEnabled ? undefined : 'light' }
        >
          <Bootstrap
            session={ pageProps?.session }
            container={ container }
            cookies={ pageProps?.cookies }
          >
            <MetaTagsContextProvider>
              <PageLoadInstrumentation>
                <GlobalProps { ...pageProps }>
                  <FiltersContextWrapper>
                    <TimeZoneContextProvider>
                      <DomainRegistryProvider
                        container={ container }
                        domainDictionaries={ pageProps?.domainDictionaries }
                      >
                        <ApplicationGlobalProps
                          dictionaries={ pageProps?.dictionaries }
                          userPlanState={ pageProps?.userPlanState }
                          timeZoneInfo={ pageProps?.timeZoneInfo }
                          pageDictionaries={ pageProps?.pageDictionaries }
                          headerMenuItems={ pageProps?.headerMenuItems }
                          domainDictionaries={ pageProps?.domainDictionaries }
                        >
                          <DefaultSeo />
                          <div className={ cn(localStyles.layoutWrapper, roboto.variable, openSans.variable) }>
                            <div id={ PROGRESS_BAR_PARENT_ID } className={ localStyles.progressBarParent } />
                            {
                              isFullWidth
                                ? (
                                  <PageNavigationInstrumentation>
                                    <PagesProgressProvider { ...pagesProgressProps }>
                                      <PageComponent { ...pageProps } />
                                    </PagesProgressProvider>
                                  </PageNavigationInstrumentation>
                                )
                                : (
                                  <AiSidebarContextProvider initialState={ pageProps?.aiSidebarState }>
                                    <Layout
                                      breadcrumbs={ pageProps.breadcrumbs }
                                      footerConfig={ footerCollection }
                                    >
                                      <PageNavigationInstrumentation>
                                        <PagesProgressProvider { ...pagesProgressProps }>
                                          <PageComponent { ...pageProps } />
                                        </PagesProgressProvider>
                                      </PageNavigationInstrumentation>
                                    </Layout>
                                    <FeatureToggle featureName={ ASK_AI }>
                                      <AskAiChatProvider>
                                        <AiSidebar />
                                      </AskAiChatProvider>
                                    </FeatureToggle>
                                  </AiSidebarContextProvider>
                                )
                            }
                          </div>
                          <CookiesBanner />
                        </ApplicationGlobalProps>
                      </DomainRegistryProvider>
                    </TimeZoneContextProvider>
                  </FiltersContextWrapper>
                </GlobalProps>
              </PageLoadInstrumentation>
            </MetaTagsContextProvider>
          </Bootstrap>
        </ThemeProvider>
      </ErrorBoundary>
    </>
  );
};

const App = configureApp(AppComponent, rootModules) as AppType;

// Workaround: keeps the CSP nonce on pages like /404 (vercel/next.js#64023).
App.getInitialProps = async (appContext: AppContext): Promise<AppInitialProps> => {
  const appProps = await NextApp.getInitialProps(appContext);
  return appProps;
};

export default App;
